/**
 * "Dit is een interventie" in the fase dialog. Checks whether the Odoo
 * `Interventie` tag exists; when it does not, the box is disabled with a hint —
 * the portal never creates the tag, Igor does that in Odoo. A failed check is a
 * retryable error, never the missing-tag hint.
 */
import { useEffect, useState } from 'react';
import { fetchInterventieTagAvailable } from '@/api/projectFases';

interface Props {
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
}

export function FaseInterventieCheckbox({ checked, onChange, disabled = false }: Props) {
  // 'error' = Odoo could not be asked: NOT the same as a missing tag, so it gets a
  // retry instead of the "maak eerst het label aan" hint.
  const [state, setState] = useState<'loading' | 'available' | 'missing' | 'error'>('loading');
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let live = true;
    setState('loading');
    fetchInterventieTagAvailable()
      .then((a) => { if (live) setState(a ? 'available' : 'missing'); })
      .catch(() => { if (live) setState('error'); });
    return () => { live = false; };
  }, [attempt]);

  return (
    <div>
      <label className="flex items-center gap-2 text-sm text-td-navy">
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          disabled={disabled || state !== 'available'}
          className="accent-td-purple"
        />
        Dit is een interventie
      </label>
      {state === 'missing' && (
        <p className="mt-1 text-xs text-td-muted">
          Maak eerst het label &apos;Interventie&apos; aan in Odoo (Project → Configuratie → Labels).
        </p>
      )}
      {state === 'error' && (
        <p className="mt-1 text-xs text-red-600" role="alert">
          Het label Interventie kon niet worden opgehaald.{' '}
          <button type="button" onClick={() => setAttempt((n) => n + 1)} className="font-semibold text-td-blue hover:underline">
            Opnieuw proberen
          </button>
        </p>
      )}
    </div>
  );
}
